// The admin person screens' calls to the backend, all under /api/cms/people/** (the admin area: admins and the team, authority.StaffRoutes):
// person.PersonAdminController, person.PersonChangeSetController and person.PersonResearchController.
// Every function answers the response body; the axios helper is the one the other admin screens use
// (it attaches the admin JWT and refreshes it). Kept in one module so the screens can be tested
// against a mocked API (the tests mock this module and assert on the payloads, not on axios), the
// same shape ProgramAdminApi has.
//
// The list filters are the query the list and the CSV share (PersonDirectoryService.Filter):
// search (name or email), status and interest (repeatable; the service matches any of them),
// hasAccount and eventId. Sorting is whitelisted by the service.
import api from '../../utils/authUtils';

// The filter query, with the empty pieces left out (so the URL carries only what was chosen).
export const filterParams = (filters = {}) => {
  const params = {};
  if (filters.search) params.search = filters.search;
  if (filters.statuses && filters.statuses.length) params.status = filters.statuses;
  if (filters.interests && filters.interests.length) params.interest = filters.interests;
  if (filters.hasAccount === true || filters.hasAccount === false) params.hasAccount = filters.hasAccount;
  if (filters.eventId) params.eventId = filters.eventId;
  return params;
};

// The matches query: the typed name and email, without the blank pieces.
export const matchParams = ({ firstName, lastName, email } = {}) => {
  const params = {};
  if (firstName && firstName.trim()) params.firstName = firstName.trim();
  if (lastName && lastName.trim()) params.lastName = lastName.trim();
  if (email && email.trim()) params.email = email.trim();
  return params;
};

// PersonAdminController.list -> PersonDirectoryService.Page
export const fetchPeople = (filters, { sort, dir, page, size } = {}) => api
  .get('/api/cms/people', { params: { ...filterParams(filters), sort, dir, page, size } }).then((r) => r.data);

// PersonAdminController.statistics -> PersonDirectoryService.Statistics
export const fetchStatistics = () => api.get('/api/cms/people/statistics').then((r) => r.data);

// PersonAdminController exportCsv: the filtered list as a CSV download (the same filters as the list).
export const exportPeopleCsv = (filters) => api
  .get('/api/cms/people/export.csv', { params: filterParams(filters), responseType: 'blob' }).then((r) => r.data);

// program.EventOptionsController: the event filter's dropdown
export const fetchEventOptions = () => api.get('/api/cms/events/options').then((r) => r.data);

// PersonAdminController.detail -> PersonDetailService.DetailView
export const fetchPerson = (id) => api.get(`/api/cms/people/${id}`).then((r) => r.data);

// PersonAdminController.matches: "who could this be?" before a person is created by hand
// (PersonMatcher, read-only) -> {status: EMAIL|NAME|AMBIGUOUS|SIMILAR|NONE, candidates: [{personId,
// programName, institution, contactEmail, hasAccount, reason}]}. Blank pieces are left out.
export const fetchPersonMatches = ({ firstName, lastName, email } = {}) => api
  .get('/api/cms/people/matches', { params: matchParams({ firstName, lastName, email }) }).then((r) => r.data);

// PersonAdminController.create: a new person (origin ADMIN) from {field: value, ...} (camelCase);
// answers the new person's detail (201). A contact email another live person has is a 409.
export const createPerson = (fields) => api.post('/api/cms/people', fields).then((r) => r.data);

// PersonAdminController.edit: an ADMIN change set from {field: value, ...} (camelCase). On an account
// holder only internalNotes and position are accepted (D33); any other field is a 409. With
// { force: true } ("Apply directly", owner decision 2026-10-20) the request carries ?force=true and an
// account holder's fields are written at once without asking them; contactEmail is then a 400 (the login).
export const savePerson = (id, fields, { force = false } = {}) => (force
  ? api.put(`/api/cms/people/${id}`, fields, { params: { force: true } })
  : api.put(`/api/cms/people/${id}`, fields)).then((r) => r.data);

// PersonAdminController.uploadPhoto: the admin's photo for the person, multipart "file". It is an
// ADMIN change set like an edit: applied at once without an account, waiting for the person's next
// invitation with one (never a 409). Answers the person's detail.
export const uploadPersonPhoto = (id, file) => {
  const body = new FormData();
  body.append('file', file);
  return api.post(`/api/cms/people/${id}/photo`, body).then((r) => r.data);
};

// PersonChangeSetController.decide: several fields at once (explicit save), then apply. The map is
// {fieldKey: 'ACCEPT' | 'REJECT'}.
export const decideChangeSet = (personId, changeSetId, decisions) => api
  .post(`/api/cms/people/${personId}/change-sets/${changeSetId}/decisions`, { decisions }).then((r) => r.data);

// PersonChangeSetController.close: close without applying the rest.
export const closeChangeSet = (personId, changeSetId) => api
  .post(`/api/cms/people/${personId}/change-sets/${changeSetId}/close`).then((r) => r.data);

// PersonResearchController: start for one person (202 with the job) and the person's jobs.
export const startResearch = (personId, purpose = 'GENERAL') => api
  .post(`/api/cms/people/${personId}/research`, { purpose }).then((r) => r.data);

export const fetchResearchJobs = (personId) => api
  .get(`/api/cms/people/${personId}/research-jobs`).then((r) => r.data);

// publicprofile.PublicProfileAdminController: the person's public profile as the admin sees it -
// {hidden, hiddenAt, hiddenBy, suggestions, confirmed, rejected} (design study-feed §6.2).
export const fetchPublicProfile = (id) => api.get(`/api/cms/people/${id}/public-profile`).then((r) => r.data);

// Only an admin can switch a public profile off (S11).
export const setPublicProfileHidden = (id, hidden) => api
  .put(`/api/cms/people/${id}/public-profile/hidden`, { hidden }).then((r) => r.data);

// decision: 'CONFIRMED', 'REJECTED' or null (removes the decision); decided as ADMIN.
export const decidePublicProfileStudy = (id, pmid, decision) => api
  .put(`/api/cms/people/${id}/public-profile/studies/${encodeURIComponent(pmid)}`, { decision }).then((r) => r.data);

// person.PersonMergeController (admins only): what merging absorbedId into survivorId would do ->
// {survivorName, absorbedName, refused, refusal, survivorLogin, absorbedLogin, removesLogin, fills,
// conflicts, movedRows}, and the merge itself. With two logins, removeAbsorbedLogin confirms that the
// absorbed person's login is deleted.
export const previewMerge = (survivorId, absorbedId) => api
  .get(`/api/cms/people/${survivorId}/merge-preview`, { params: { absorbedId } }).then((r) => r.data);

export const mergePeople = (survivorId, absorbedId, { removeAbsorbedLogin = false } = {}) => api
  .post(`/api/cms/people/${survivorId}/merge`, { absorbedId, removeAbsorbedLogin }).then((r) => r.data);

// person.PersonDeletionController (admins only): {deletable, blockers, removedWithIt, clearedProposals},
// and the delete (204; 409 when something is attached).
export const fetchDeletion = (personId) => api.get(`/api/cms/people/${personId}/deletion`).then((r) => r.data);

export const deletePerson = (personId) => api.delete(`/api/cms/people/${personId}`).then(() => null);
